<template>
  <div>
    <VcDropdown
      v-bind="$attrs"
      :max-height="screen === SCREENS.MOBILE_CUSTOM_INSTRUCTIONS ? '' : '500px'"
      :position-x="anchorX"
      :position-y="anchorY + anchorOffsetY"
      :value="dropdownState"
      z-index="205"
      @input="this.toggleDropdownMenuOpen"
      :class="{ 'full-height': screen === SCREENS.MOBILE_CUSTOM_INSTRUCTIONS }"
    >
      <template #activator="{ on, attrs }">
        <slot name="activator" v-bind="attrs" v-on="on" />
      </template>
      <template #header>
        <VcLayout v-if="screen === SCREENS.SHOW_ALL_OPTIONS || screen === SCREENS.CUSTOM_GENERATE">
          <VcMenuItem
            :label="subMenuHeaderTitle"
            :icon="rtl ? 'icon-Chevron-right': 'icon-Chevron-left'"
            size="sm"
            @click="onClickBackMainMenu"
          ></VcMenuItem>
        </VcLayout>

        <VcLayout v-if="screen === SCREENS.MOBILE_CUSTOM_INSTRUCTIONS"
                  align-center
                  justify-space-between
                  class="custom-instructions-header pa-4"
        >
          <VcLayout align-center>
            <VcButton icon class="mr-3" @click="onClickBackMainMenu">
              <template #prepend>
                <VcIcon size="16">icon-Chevron-left</VcIcon>
              </template>
            </VcButton>
            <div class="mobile-custom-instructions-title">
              {{ getBackButtonLabel }}
            </div>
          </VcLayout>
          <VcButton icon @click="onMobileCustomInstructionsClose">
            <template #prepend>
              <VcIcon size="16">icon-close_new</VcIcon>
            </template>
          </VcButton>
        </VcLayout>
      </template>
      <template #default>
        <VcLayout
          ref="dropdownContent"
          class="dropdown-content-wrapper"
          column
          :class="{ 'mobile-instructions': screen === SCREENS.MOBILE_CUSTOM_INSTRUCTIONS }"
        >
          <VcLayout justify-center align-center>
            <VcLoader class="loader" v-if="!promptGroups.length && !error.text" />
          </VcLayout>

          <VcLayout v-if="screen === SCREENS.MAIN_MENU">
            <VcLayout column>
              <VcAlert
                class="alert-error"
                v-if="error.text || error.header"
                :type="error.type"
                :header-text="error.header"
                :body-text="error.text"
                :is-inline-header=true
              />

              <VcAlert
                class="alert-error"
                v-if="missingRequiredVariables.length > 0"
                type="attention"
                :body-text="$t('ai_content_generator.errors.missing_required_variables.text', {
                  comma_separated_fields : missingRequiredVariables.join(', '),
                  })"
                :is-inline-header=true
              />

              <VcAlert
                class="alert-error"
                v-if="infoBannerText && showInfoBanner"
                type="info"
                :body-text="infoBannerText"
                :is-inline-header=true
                :close-button="true"
                @closeAlert="closeInfoBanner"
              />

              <VcGroupedItems :item-groups="menuItems" show-dividers @change="onClickPromptItem">
                <template #item="{ item }">
                  <MenuItem :item="item" data-qa="prompt-menu-item" />
                </template>
              </VcGroupedItems>
            </VcLayout>
          </VcLayout>

          <VcLayout v-else-if="screen === SCREENS.SHOW_ALL_OPTIONS">
            <VcGroupedItems :item-groups="selectedPromptGroup.moreItems"
                            @change="onClickPromptItem">
              <template #item="{ item }">
                <MenuItem :item="item" />
              </template>
            </VcGroupedItems>
          </VcLayout>

          <VcLayout v-else-if="screen === SCREENS.CUSTOM_GENERATE">
            <CustomInstructions @generate="onCustomGenerate" />
          </VcLayout>

          <VcLayout v-if="screen === SCREENS.MOBILE_CUSTOM_INSTRUCTIONS" class="mobile-custom-instructions-layout">
            <MobileCustomInstructions
              :current-message="mobileCurrentMessage"
              :generated-reply="streamText"
              @insertReply="onMobileInsertReply"
              @cancelAiGeneration="onMobileCancelAiGeneration"
            />
          </VcLayout>

          <VcLayout v-if="disclaimerText" column>
            <VcDivider class="ma-0" />
            <p class="my-3 mx-4 disclaimer">{{ disclaimerText }}</p>
          </VcLayout>
          <!-- Mobile input field at bottom of menu (only when feature flag enabled) -->
          <VcDivider v-if="showCustomInstructionsTextField" class="ma-0"/>
          <MobileCustomInstructionsInput
            v-show="showCustomInstructionsTextField"
            v-model="mobileInstructionsInput"
            :screen="screen"
            :screens="SCREENS"
            @focus="onMobileInputFocus"
            @generate="onMobileCustomGenerate"
            ref="mobileCustomInstructionsInput"
          />
        </VcLayout>
      </template>
    </VcDropdown>
  </div>
</template>

<script>
import MenuItem from '@/modules/aiContentGenerator/components/MenuItem.vue';
import CustomInstructions from '@/modules/aiContentGenerator/components/CustomInstructions.vue';
import MobileCustomInstructions
  from '@/modules/aiContentGenerator/components/MobileCustomInstructions.vue';
import MobileCustomInstructionsInput
  from '@/modules/aiContentGenerator/components/MobileCustomInstructionsInput.vue';
import { mapActions, mapGetters } from 'vuex';
import storageService from '@/modules/common/services/storageService';
import getAiGeneratorPrompts from '../constants/prompts';

const SHOW_MORE_OPTIONS_TRESHOLD = 4;
const SHOW_MORE_OPTIONS_NUMBER_TO_SHOW = 3;
const ITEM_SHOW_ALL_OPTIONS_SUFFIX = '-show-all-options';
const SMART_REPLY_FEATURE = 'smart-reply';

export const SCREENS = {
  MAIN_MENU: 'SCREEN_MAIN_MENU',
  SHOW_ALL_OPTIONS: 'SCREEN_SHOW_ALL_OPTIONS',
  CUSTOM_GENERATE: 'SCREEN_CUSTOM_GENERATE',
  MOBILE_CUSTOM_INSTRUCTIONS: 'SCREEN_MOBILE_CUSTOM_INSTRUCTIONS',
};

function isCustomInstructionPrompt(prompt) {
  return prompt.metadata?.configuration_key === 'rewrite_with_custom_instructions';
}

export default {
  name: 'AiGenerator',
  components: {
    MenuItem,
    CustomInstructions,
    MobileCustomInstructions,
    MobileCustomInstructionsInput,
  },
  props: {
    agentName: {
      type: String,
      required: true,
    },
    modelName: {
      type: String,
      required: false,
      validator: (value) => ['gpt-4'].includes(value),
    },
    feature: {
      type: String,
      required: true,
    },
    variables: {
      type: Object,
      default: () => ({}),
      validator: (props) => Object.values(props).every((v) => typeof v === 'object' && 'value' in v && 'title' in v),
    },
    targetTextValue: {
      type: String,
      default: '',
    },
    dropdownState: {
      type: Boolean,
      default: false,
    },
    infoBannerText: {
      type: String,
      default: '',
    },
    disclaimerText: {
      type: String,
      default: '',
    },
    analyticsParameters: {
      type: Object,
      default: () => ({}),
    },
    anchorX: {
      type: Number,
      default: 0,
    },
    anchorY: {
      type: Number,
      default: 0,
    },
    overrideGenerateOutput: {
      type: String,
    },
  },
  data() {
    const showInfoBanner = storageService.get('ai-generator/info-banner') !== 'false';
    return {
      SCREENS,
      screen: SCREENS.MAIN_MENU,
      selectedPromptGroup: null,
      subMenuHeaderTitle: null,
      selectedPromptItem: null,
      anchorOffsetY: 0,
      showInfoBanner,
      clickedCustomGeneratePrompt: null,
      mobileInstructionsInput: '',
    };
  },
  created() {
    this.addI18nModule('ai-content-generator-pov');
    this.SCREENS = SCREENS;
  },
  computed: {
    ...mapGetters('AiGeneratorStore', {
      fetchPrompts: 'promptValue',
      fetchText: 'textValue',
      fetchError: 'errorValue',
      fetchIsStreaming: 'isStreaming',
      fetchChatUid: 'chatUid',
    }),
    ...mapGetters('BusinessStore', ['locale']),
    promptGroups() {
      // For smart-reply feature, use hardcoded prompts (already in group structure)
      const hardcodedPrompts = getAiGeneratorPrompts()[this.feature];
      if (hardcodedPrompts) {
        return hardcodedPrompts;
      }

      // For other features, fall back to fetched prompts from store
      return this.fetchPrompts(this.feature) || [];
    },
    error() {
      const err = this.fetchError(this.feature);
      if (err) {
        return {
          header: this.$t(err.header),
          text: this.$t(err.text),
          type: err.type,
        };
      }
      return {
        header: '',
        text: '',
      };
    },
    streamText() {
      return this.fetchText(this.feature);
    },
    isStreaming() {
      return this.fetchIsStreaming(this.feature);
    },
    hasActiveChat() {
      return Boolean(this.fetchChatUid(this.feature));
    },
    rtl() {
      return this.$vuetify.rtl;
    },
    isMobile() {
      return this.$isMobileLayout();
    },
    hasCustomInstructionsFF() {
      return this.$hasFeature('rollout.ai.custom_instructions');
    },
    isMobileWithNewFF() {
      return this.isMobile && this.hasCustomInstructionsFF && this.feature === SMART_REPLY_FEATURE;
    },
    getBackButtonLabel() {
      if (this.screen === SCREENS.MOBILE_CUSTOM_INSTRUCTIONS) {
        return this.$t('ai_content_generator.custom_instructions.back_button_label');
      }
      return this.subMenuHeaderTitle || this.$t('ai_content_generator.show_all.back_to_menu');
    },
    mobileCurrentMessage() {
      // Show generated reply if available, otherwise show original message
      return this.streamText || this.targetTextValue;
    },
    menuItems() {
      const menuExpandIcon = (this.$vuetify.rtl ? 'icon-Chevron-left' : 'icon-Chevron-right');
      const hasMissingRequiredVariables = this.missingRequiredVariables.length > 0;
      const items = [];

      // Filter groups based on mobile visibility when on mobile with feature flag
      const groupsToShow = this.isMobileWithNewFF
        ? this.promptGroups.filter((group) => group.showInMobile === true)
        : this.promptGroups.filter((group) => group.showInMobile !== true);

      items.push(...groupsToShow.map((group) => ({
        id: group.uid, // Design system uses id's for the items but V3 standards use uid
        ...group,
        label: group.title || ' ',
        items: group.prompts
          .map((prompt) => ({
            id: prompt.uid,
            prompt,
            icon: prompt.icon,
            rightIcon: this.showMenuItemRightIcon(prompt) ? menuExpandIcon : null,
            disabled: hasMissingRequiredVariables,
            handler: () => this.onClickPrompt(prompt),
          })).sort((promptItemA, promptItemB) => promptItemA.prompt.order - promptItemB.prompt.order), // Sorting step based on prompt.order
      })).sort((groupA, groupB) => groupA.order - groupB.order));

      // Add "show all options" item for each group (skip for smart-reply which shows all prompts)
      if (this.feature !== SMART_REPLY_FEATURE) {
        for (let i = 0; i < items.length; i++) {
          const promptGroup = items[i];
          if (promptGroup.items.length > SHOW_MORE_OPTIONS_TRESHOLD) {
            promptGroup.moreItems = [{
              id: `${promptGroup.uid}-more`,
              items: promptGroup.items.slice(SHOW_MORE_OPTIONS_NUMBER_TO_SHOW),
            }];
            promptGroup.items = promptGroup.items.slice(0, SHOW_MORE_OPTIONS_NUMBER_TO_SHOW);
            promptGroup.items.push({
              id: `${promptGroup.uid}${ITEM_SHOW_ALL_OPTIONS_SUFFIX}`,
              prompt: {
                title: this.$t('ai_content_generator.show_all.menu_item_title'),
              },
              icon: null,
              disabled: hasMissingRequiredVariables,
              rightIcon: menuExpandIcon,
              handler: () => this.onClickShowAllOptions(promptGroup),
            });
          }
        }
      }

      // set visibility and disable state for each item depending on the targetTextValue
      const hasTextContent = !!this.targetTextValue;
      for (let i = 0; i < items.length; i++) {
        const group = items[i];
        if (group.metadata?.configuration_key === 'generate') {
          if (hasTextContent) {
            group.items = group.items.filter((item) => item.prompt.metadata?.configuration_key === 'rewrite' || item.prompt.metadata?.configuration_key === 'rewrite_with_custom_instructions' || !item.prompt.metadata?.configuration_key);
          } else {
            group.items = group.items.filter((item) => item.prompt.metadata?.configuration_key === 'generate' || item.prompt.metadata?.configuration_key === 'rewrite_with_custom_instructions' || !item.prompt.metadata?.configuration_key);
          }
          group.items = group.items.map((item) => ({ ...item, disabled: !hasTextContent && item.prompt.disableWhenNoText }));
        } else {
          for (let j = 0; j < group.items.length; j++) {
            const promptItem = group.items[j];
            promptItem.disabled = hasMissingRequiredVariables
              || !hasTextContent
              || this.targetTextValue.length < 2;
          }
        }
      }

      return items;
    },
    missingRequiredVariables() {
      const missingVars = new Set();
      const ignoreVariables = ['populated_text', 'custom_instructions'];
      this.promptGroups.forEach((group) => {
        group.prompts.forEach((prompt) => {
          prompt.requiredVariables.forEach((variable) => {
            if (ignoreVariables.includes(variable)) {
              return;
            }
            if (!this.variables[variable]?.value) {
              missingVars.add(this.variables[variable]?.title);
            }
          });
        });
      });

      return Array.from(missingVars);
    },
    showCustomInstructionsTextField() {
      return this.isMobile
        && this.hasCustomInstructionsFF
        && (this.screen === SCREENS.MAIN_MENU || this.screen === SCREENS.MOBILE_CUSTOM_INSTRUCTIONS);
    },
  },
  methods: {
    // Keep existing actions for backward compatibility with other features
    ...mapActions('AiGeneratorStore', [
      'sendMessage',
      'createChat',
      'getPrompts',
      'generateSmartReply',
      'cancelGeneration',
    ]),
    closeInfoBanner() {
      this.showInfoBanner = false;
      storageService.save('ai-generator/info-banner', 'false');
    },
    observeDropdownResize() {
      const { dropdownContent } = this.$refs;
      if (this.resizeObserver || !dropdownContent?.$el) return;
      this.resizeObserver = new ResizeObserver(() => {
        this.adjustDropdownPosition();
      });

      this.resizeObserver.observe(dropdownContent.$el);
    },
    adjustDropdownPosition() {
      const { dropdownContent } = this.$refs;
      const dropdownRect = dropdownContent.$el.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const isDropdownExceedingViewport = dropdownRect.bottom > viewportHeight;

      if (isDropdownExceedingViewport) {
        this.anchorOffsetY = (viewportHeight - dropdownRect.height - 8) - this.anchorY;
      } else {
        this.anchorOffsetY = 0;
      }
    },
    toggleDropdownMenuOpen(state) {
      this.$emit('dropdownStateChange', typeof state === 'boolean' ? state : !this.dropdownState);
    },
    onClickOpenCustomGenerate(prompt) {
      this.subMenuHeaderTitle = prompt.title;
      this.clickedCustomGeneratePrompt = prompt;
      // Only close dropdown and emit event if new custom instructions feature flag is enabled
      // Otherwise, keep dropdown open and show CustomInstructions component inside (old behavior)
      if (this.hasCustomInstructionsFF) {
        this.toggleDropdownMenuOpen(false);
        this.$emit('openCustomInstructions', prompt);
      } else {
        // Old behavior: show CustomInstructions component inside the dropdown menu
        this.screen = SCREENS.CUSTOM_GENERATE;
      }
    },
    onCustomGenerate(userInstructions) {
      // Close the dropdown and send the prompt with custom instructions
      this.toggleDropdownMenuOpen(false);
      if (this.clickedCustomGeneratePrompt) {
        this.sendPrompt(this.clickedCustomGeneratePrompt, userInstructions);
      }
      this.clickedCustomGeneratePrompt = null;
    },
    onClickPrompt(prompt) {
      if (isCustomInstructionPrompt(prompt)) {
        this.onClickOpenCustomGenerate(prompt);
      } else {
        this.toggleDropdownMenuOpen(false);
        this.sendPrompt(prompt);
      }
    },
    onClickShowAllOptions(group) {
      this.selectedPromptGroup = group;
      this.subMenuHeaderTitle = this.$t('ai_content_generator.show_all.back_to_menu');
      this.screen = SCREENS.SHOW_ALL_OPTIONS;
    },
    onClickBackMainMenu() {
      if (this.screen === SCREENS.CUSTOM_GENERATE || this.screen === SCREENS.MOBILE_CUSTOM_INSTRUCTIONS) {
        this.clickedCustomGeneratePrompt = null;
        this.mobileInstructionsInput = '';
      }
      this.screen = SCREENS.MAIN_MENU;
      this.selectedPromptGroup = null;
    },
    onMobileInputFocus() {
      if (!this.isMobileWithNewFF) return;
      // If user focuses the input, switch to custom instructions view
      if (this.screen === SCREENS.MAIN_MENU) {
        // Find the rewrite_with_custom_instructions prompt
        const customPrompt = this.promptGroups
          .flatMap((group) => group.prompts)
          .find((prompt) => prompt.metadata?.configuration_key === 'rewrite_with_custom_instructions');

        if (customPrompt) {
          this.clickedCustomGeneratePrompt = customPrompt;
          this.subMenuHeaderTitle = this.$t('ai_content_generator.show_all.back_to_menu');
          this.screen = SCREENS.MOBILE_CUSTOM_INSTRUCTIONS;
        }
      }
    },
    onMobileCustomGenerate() {
      if (this.clickedCustomGeneratePrompt) {
        this.sendPrompt(this.clickedCustomGeneratePrompt, this.mobileInstructionsInput);
      }
    },
    onMobileInsertReply() {
      if (this.streamText) {
        this.$emit('insertReply', this.streamText);
        this.toggleDropdownMenuOpen(false);
      }
    },
    onMobileCustomInstructionsClose() {
      this.mobileInstructionsInput = '';
      this.screen = SCREENS.MAIN_MENU;
      this.clickedCustomGeneratePrompt = null;
      this.toggleDropdownMenuOpen(false);
    },
    onMobileCancelAiGeneration() {
      this.cancelGeneration({ feature: this.feature });
    },
    onClickPromptItem(itemId) {
      const item = this.findItemById(itemId);
      if (!item) {
        return;
      }
      this.$track('ai-generator-prompt-clicked', {
        ...this.analyticsParameters,
        feature: this.feature,
        title: item.prompt.title,
      });
      this.$emit('promptClicked', item.prompt);
      this.selectedPromptItem = item;
      // Check if the prompt is a generate and not a re-generate prompt
      if (this.overrideGenerateOutput && item.prompt.metadata?.configuration_key === 'generate') {
        // send result as if it was generated by the endpoint
        this.$emit('onOverrideGenerateOutput', this.overrideGenerateOutput);
      } else {
        item.handler();
      }
    },
    findItemById(itemId) {
      for (let i = 0; i < this.menuItems.length; i++) {
        const group = this.menuItems[i];
        const foundItem = group.items.find((item) => item.id === itemId);
        if (foundItem) return foundItem;

        const foundMoreItem = group.moreItems?.[0]?.items.find((item) => item.id === itemId);
        if (foundMoreItem) return foundMoreItem;
      }
      return null;
    },
    async startStream(item) {
      this.$emit('startStream', item);
    },
    async onStream(value) {
      this.$emit('onStream', value);
    },
    async endStream(data) {
      this.$emit('endStream', data || { data: '' });
    },
    async sendPrompt(item, customInstructions = null) {
      // Smart-reply uses direct service call with specific parameters
      if (this.feature === SMART_REPLY_FEATURE) {
        const matterUid = this.variables.matter_uid?.value;
        const clientUid = this.variables.client_uid?.value;

        // Call new generateSmartReply action which uses smart reply service directly
        await this.generateSmartReply({
          feature: this.feature,
          matterUid,
          clientUid,
          promptUid: item.uid,
          populatedText: this.targetTextValue,
          customInstructions,
        });
      } else {
        // For other features, use the legacy chat-based approach
        // case chat creation has failed, stop the operation
        if (!await this.ensureActiveChat()) return;

        const variables = this.prepareVariables(item, customInstructions);
        await this.sendMessage({
          feature: this.feature,
          content: {
            promptUid: item.uid,
            variables,
          },
        });
      }
    },
    prepareVariables(item, customInstructions) {
      // for each variable extract the value property
      const requiredVariableValues = {};
      item.requiredVariables.forEach((variable) => {
        if (this.variables[variable]) {
          requiredVariableValues[variable] = this.variables[variable].value;
        }
      });
      item.optionalVariables.forEach((variable) => {
        if (this.variables[variable]) {
          requiredVariableValues[variable] = this.variables[variable].value;
        }
      });
      return {
        populated_text: this.targetTextValue,
        ...requiredVariableValues,
        custom_instructions: customInstructions,
      };
    },
    async ensureActiveChat() {
      if (!this.hasActiveChat) {
        await this.createChat({
          agentName: this.agentName,
          feature: this.feature,
          windowSize: 0,
          modelName: this.modelName,
        });
      }
      return this.hasActiveChat;
    },
    showMenuItemRightIcon(prompt) {
      return !this.hasCustomInstructionsFF && isCustomInstructionPrompt(prompt);
    },
  },
  watch: {
    streamText(newVal) {
      if (newVal) {
        this.onStream(newVal);
      }
    },
    isStreaming(newVal) {
      if (newVal === true) {
        this.startStream(this.selectedPromptItem);
      } else if (newVal === false) {
        this.endStream(this.streamText);
      }
    },
    async dropdownState(newVal) {
      if (newVal) {
        // For smart-reply, prompts are hardcoded. For other features, fetch from API
        const hasHardcodedPrompts = !!getAiGeneratorPrompts()[this.feature];
        if (!hasHardcodedPrompts) {
          await this.getPrompts({ feature: this.feature, locale: this.locale });
        }

        this.$track('ai-generator-button-clicked', {
          ...this.analyticsParameters,
          feature: this.feature,
          isInvalid: this.missingRequiredVariables?.length > 0,
        });

        this.observeDropdownResize();

        this.$emit('onDropdownOpen', {
          feature: this.feature,
          missingRequiredVariables: this.missingRequiredVariables,
        });
      } else {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }
        // Reset mobile input when closing
        this.mobileInstructionsInput = '';
        if (this.screen === SCREENS.MOBILE_CUSTOM_INSTRUCTIONS || this.screen === SCREENS.CUSTOM_GENERATE) {
          this.screen = SCREENS.MAIN_MENU;
        }
      }
    },
  },
};

</script>

<style scoped lang="scss">

.dropdown-content-wrapper {
  min-height: 64px;
  width: 300px;

  &.mobile-instructions {
    // 139px = custom instructions header height + mobile input field height
    height: calc(100vh - 139px);
  }
  .mobile-custom-instructions-layout {
    height: 100%;
  }
  .loader {
    margin-top: var(--size-value3);
    margin-right: var(--size-value3);
    margin-left: var(--size-value3);
  }
}

::v-deep .v-divider {
  margin: 0 16px;
}

::v-deep .grouped-items__group__header.mb-2 {
  font-size: 10px !important;
  padding-top: 4px !important;
  padding-left: 16px !important;
  padding-right: 12px !important;
  margin-bottom: 0 !important;
}

.alert-error {
  margin: var(--size-value2);
}
.disclaimer {
  color: var(--gray-darken-4);
  font-size: var(--font-size-xx-small);
  font-weight: var(--font-weight-medium);
}

.mobile-custom-instructions-title {
  font-weight: var(--font-weight-large);
  font-size: var(--font-size-small2);
  padding-inline-end: var(--size-value6);
}

.custom-instructions-header {
  border-bottom: 1px solid var(--gray-lighten-1);
}
</style>
